import React, {useEffect, useReducer} from 'react'
import styled from 'styled-components'
import {
  StyledWorkspaceActionButton as ActionButtonContainer,
  StyledWorkspaceContainer as Container,
  StyledWorkspaceFilter as Filter,
  StyledWorkspaceFloorList as FloorListContainer,
  StyledWorkspaceFloorPlan as FloorPlanContainer,
  StyledWorkspaceHeader as WorkspaceHeader
} from './StyledWorkspace'
import WorkspaceMiniFilter from './Filter/WorkspaceMiniFilter'
import FloorList, {DIRECTION} from '../../components/FloorList/FloorList'
import FloorPlan from '../FloorPlan/FloorPlan'
import ActionButton from '../../components/ActionButton/ActionButton'
import WorkspaceList from './WorkspaceList/WorkspaceList'
import Header from '../../components/Header/Header'
import MediaQuery from 'react-responsive'
import {Layer} from 'grommet/components/Layer'
import WorkspaceFilter from './Filter/WorkspaceFilter'
import {DASHBOARD, KIOSK_CONTAINER} from '../../constants/const'
import {_L, useLocaleState} from '../../context/LocaleContext'
import BuildingList from '../BuildingList/BuildingList'
import {fetchBuilding, fetchBuildingFloors} from '../../utils/buildings'
import HeaderButton from '../../components/Header/HeaderButton'
import {
  fetchAvailableRoomsByFloor,
  fetchFirstAvailableFloor,
  fetchWorkspaceAvailableRooms,
  fetchAvailableRoomList
} from '../../api/workspace'
import {useUserState} from '../../context/UserContext'
import {useUIDispatch, useUIState} from '../../context/UIContext'
import ActionTitle from '../../components/Header/ActionTitle'
import {useWorkspaceState} from '../../context/WorkspaceContext'
import {getBookingTimes, useConfigState} from '../../context/ConfigContext'
import {useDrawingDispatch, useDrawingState} from '../../context/DrawingContext'
import {useKioskTimer} from '../../hooks/kiosk-timer'
import {fetchRoom} from '../../api/room'
import {highlightAvailableRooms, resetHighlights} from '../../utils/highlight'
import isEmpty from 'lodash/isEmpty'

const ModalFilter = ({
  onCloseFilter,
  onSearch,
  responsive,
  bookingTimes,
  ...rest
}) => (
  <Layer responsive={responsive} {...rest}>
    <FilterContainer>
      <WorkspaceFilter
        bookingTimes={bookingTimes}
        showClose
        onCloseFilter={onCloseFilter}
        onSearch={onSearch}
      />
    </FilterContainer>
  </Layer>
)

const initialState = {
  floors: [],
  building: {blId: '', name: ''},
  availableRooms: {},
  showListView: false,
  showFilter: false,
  showActionButton: true,
  showBuildingList: false,
  showNotification: false,
  selectedRoom: {},
  availableRoomList: [],
  totalSpaces: 0
}

const reducer = (state, action) => {
  switch (action.type) {
    case 'setData':
      return {
        ...state,
        ...action.payload
      }
    case 'setAvailableRoomList':
      return {
        ...state,
        availableRoomList: action.payload.availableRoomList,
        totalSpaces: action.payload.totalSpaces
      }
    default:
      throw new Error('Unexpected action')
  }
}

const Workspace = ({parentPage}) => {
  const localeState = useLocaleState()
  const [state, dispatch] = useReducer(reducer, initialState)
  const {search} = useWorkspaceState()

  const {
    floors,
    availableRooms,
    building,
    showListView,
    showFilter,
    showActionButton,
    showBuildingList,
    showNotification,
    selectedRoom,
    availableRoomList, // list view
    totalSpaces
  } = state
  const {user} = useUserState()
  const uiDispatch = useUIDispatch()
  const uiState = useUIState()
  const drawingDispatch = useDrawingDispatch()
  const {status} = useDrawingState()
  const {activeFloor, homeFloor, isKiosk} = uiState

  const bookingTimes = getBookingTimes(useConfigState())

  const {blId, flId} = activeFloor
  const {hasSvgData, blId: statusBlId, flId: statusFlId} = status

  useEffect(
    () => {
      let didCancel = false

      const emId = user.employee.emId

      async function fetchWorkspaceData() {
        if (blId) {
          if (!didCancel) {
            // reset all highlights
            resetHighlights({drawingDispatch})
          }

          let [building, floors, firstFloor] = await Promise.all(
            [fetchBuilding, fetchBuildingFloors, fetchFirstAvailableFloor].map(
              fn => fn({blId, emId, user})
            )
          )

          if (!didCancel) {
            uiDispatch({
              type: 'setActiveFloor',
              payload: {blId, flId: firstFloor}
            })

            dispatch({
              type: 'setData',
              payload: {building, floors}
            })
          }
        }
      }

      fetchWorkspaceData()

      return () => {
        didCancel = true
      }
    },
    [blId, user, drawingDispatch, uiDispatch]
  )

  useEffect(
    () => {
      async function fetchData() {
        const availableRooms = await fetchWorkspaceAvailableRooms({
          search,
          user,
          blId
        })

        dispatch({
          type: 'setData',
          payload: {availableRooms}
        })

        if (availableRooms[flId]) {
          let availableRoomCodes = availableRooms[flId]
            ? availableRooms[flId].roomCodes
            : []

          highlightAvailableRooms({
            drawingDispatch,
            availableRooms: availableRoomCodes
          })

          const {
            availableRooms: availableRoomList,
            totalSpaces
          } = await fetchAvailableRoomList({
            search,
            user,
            activeFloor: {blId, flId}
          })

          dispatch({
            type: 'setData',
            payload: {availableRoomList, totalSpaces}
          })
        } else {
          resetHighlights({drawingDispatch})
        }
      }
      fetchData()
    },
    [blId, flId, user, drawingDispatch]
  )

  useEffect(
    () => {
      const showListView =
        statusBlId === blId &&
        statusFlId === flId &&
        availableRooms &&
        availableRooms[statusFlId] &&
        !hasSvgData

      dispatch({type: 'setData', payload: {showListView}})
    },
    [hasSvgData, statusBlId, statusFlId, blId, flId, availableRooms]
  )

  const BookingPrompt = ({room, availableSpaces, localeState, onBook}) => {
    const {rmId, flId, category, rmType} = room

    let availableSpaceText = ''

    let doRender = false

    let availableRooms = 0
    if (flId && !isEmpty(availableSpaces)) {
      availableRooms = availableSpaces[flId].roomCodes.filter(
        item => item.rm_id === rmId
      ).length

      availableSpaceText =
        availableRooms > 1
          ? _L({key: 'spaces', localeState})
          : _L({key: 'space', localeState})
      doRender = true
    }

    return doRender ? (
      <>
        <StyledTitle>
          {rmId} &middot; {category}
        </StyledTitle>

        <StyledSubTitle>
          {rmType} &middot; {availableRooms} {availableSpaceText}
        </StyledSubTitle>

        <StyledButton onClick={onBook} data-testid="workspace-book-button">
          {_L({key: 'Book', localeState})}
        </StyledButton>
      </>
    ) : null
  }

  const timeoutExpired = () => {
    uiDispatch({type: 'setContent', payload: {content: KIOSK_CONTAINER}})
    uiDispatch({type: 'setActiveFloor', payload: homeFloor})
  }

  const {message, onClickHandler} = useKioskTimer({
    enable: isKiosk,
    inUseDelay: 60000,
    timeoutDelay: 10000,
    timeoutExpirationHandler: timeoutExpired
  })

  const onFloorChange = ({bl_id: blId, fl_id: flId}) => {
    uiDispatch({
      type: 'setActiveFloor',
      payload: {blId, flId}
    })
  }

  const onShowList = () =>
    dispatch({type: 'setData', payload: {showListView: !showListView}})

  const onShowFilter = () =>
    dispatch({type: 'setData', payload: {showFilter: true}})

  const onCloseFilter = () =>
    dispatch({type: 'setData', payload: {showFilter: false}})

  const onCancelWorkspaceBooking = () => {
    uiDispatch({
      type: 'setContent',
      payload: {content: parentPage || DASHBOARD}
    })

    uiDispatch({
      type: 'showSearch',
      payload: {show: false, type: null, options: {}}
    })
  }

  /**
   * This fn is async because it needs to return a Promise to highlight assets
   * after the search is completed
   */
  const onSearch = async () => {
    // Always set the filter state to closed. This has no effect when the filter
    // is displayed in the desktop profile.
    // TODO: Check search results and do not close if no records are returned.

    // reset all highlights
    resetHighlights({drawingDispatch})

    // Add the building property to the search
    const {blId, flId} = activeFloor

    const availableRooms = await fetchWorkspaceAvailableRooms({
      blId,
      search,
      user
    })

    const {
      availableRooms: availableRoomList,
      totalSpaces
    } = await fetchAvailableRoomList({
      search,
      user,
      activeFloor
    })

    dispatch({
      type: 'setData',
      payload: {availableRooms, availableRoomList, totalSpaces}
    })

    if (availableRooms[flId]) {
      let availableRoomCodes = availableRooms[flId]
        ? availableRooms[flId].roomCodes
        : []

      highlightAvailableRooms({
        drawingDispatch,
        availableRooms: availableRoomCodes
      })
    }
    dispatch({type: 'setData', payload: {showFilter: false}})
  }

  const onRoomClick = async ({room}) => {
    const roomData = await fetchRoom(room)
    dispatch({type: 'setData', payload: {selectedRoom: roomData}})
  }

  /**
   * Starts the workspace booking sequence
   * @param item The workspace item from the selected list
   * @param isList True when the item is selected from the list, false when selected from the floor plan
   */
  const onBook = ({item, isList = false}) => {
    let roomCode
    if (isList) {
      const {blId, flId, rmId} = item
      roomCode = {blId, flId, rmId}
    } else {
      roomCode = selectedRoom
    }

    uiDispatch({
      type: 'setContent',
      payload: {
        content: 'WORKSPACE_BOOKING',
        props: {roomCode, isQrRequest: false},
        parentPage: isKiosk ? KIOSK_CONTAINER : parentPage || DASHBOARD
      }
    })
  }

  const onBuildingClick = () => {
    const payload = {
      showListView: false,
      showFilter: false,
      showActionButton: false,
      showBuildingList: true
    }

    dispatch({type: 'setData', payload})
  }

  const onBuildingSelect = blId => {
    return fetchAvailableRoomsByFloor({blId, user}).then(availableRooms => {
      dispatch({
        type: 'setData',
        payload: {availableRooms}
      })

      let [firstFloor] = Object.keys(availableRooms)

      if (firstFloor) {
        uiDispatch({
          type: 'setActiveFloor',
          payload: {
            blId,
            flId: ''
          }
        })

        const payload = {
          showListView: false,
          showFilter: false,
          showActionButton: true,
          showBuildingList: false
        }
        dispatch({type: 'setData', payload})
      } else {
        dispatch({type: 'setData', payload: {showNotification: true}})
      }
    })
  }

  const onCloseNotification = () =>
    dispatch({type: 'setData', payload: {showNotification: false}})

  return (
    <Container onClick={onClickHandler}>
      <WorkspaceHeader>
        <Header
          leftContent={
            <ActionTitle
              title={_L({key: 'Workspace Booking', localeState})}
              buildingName={building.name || ''}
            />
          }
          rightContent={
            <HeaderButton
              label={_L({key: 'Cancel', localeState})}
              onClick={onCancelWorkspaceBooking}
            />
          }
        />
      </WorkspaceHeader>
      {!showBuildingList && (
        <FloorListContainer>
          <MediaQuery query="only screen and (max-width:1023px) and (orientation: portrait)">
            {matches => (
              <FloorList
                buildingCode={blId || ''}
                floorList={floors}
                itemsPerFloor={availableRooms}
                onSelected={onFloorChange}
                direction={matches ? DIRECTION.HORIZONTAL : DIRECTION.VERTICAL}
                availableOnly
                selectedFloor={activeFloor}
                onBuildingClick={onBuildingClick}
                showBuilding
              />
            )}
          </MediaQuery>
        </FloorListContainer>
      )}
      {showBuildingList ? (
        <BuildingList
          onBuildingSelect={onBuildingSelect}
          showNotification={showNotification}
          onCloseNotification={onCloseNotification}
        />
      ) : (
        <Filter>
          <MediaQuery query="only screen and (max-device-width: 1023px) and (orientation: portrait)">
            {matches => {
              if (matches) {
                return (
                  <WorkspaceMiniFilter
                    onShowFilter={onShowFilter}
                    onSearch={onSearch}
                  />
                )
              } else {
                return (
                  <WorkspaceFilter
                    onSearch={onSearch}
                    bookingTimes={bookingTimes}
                  />
                )
              }
            }}
          </MediaQuery>
        </Filter>
      )}
      {showListView ? (
        <WorkspaceList
          user={user}
          onBook={onBook}
          activeFloor={activeFloor}
          availableRoomList={availableRoomList}
          totalSpaces={totalSpaces}
        />
      ) : (
        <FloorPlanContainer showBuildingList={showBuildingList}>
          <FloorPlan
            promptContents={BookingPrompt({
              room: selectedRoom,
              availableSpaces: availableRooms,
              localeState,
              onBook
            })}
            onRoomClick={onRoomClick}
          />
        </FloorPlanContainer>
      )}
      {showActionButton && (
        <ActionButtonContainer>
          <ActionButton
            icon={showListView ? 'FLOOR_PLAN' : 'LIST'}
            backgroundColor="white"
            height={64}
            width={64}
            onClick={onShowList}
          />
        </ActionButtonContainer>
      )}
      {showFilter && (
        <MediaQuery maxDeviceWidth={420}>
          {matches => (
            <ModalFilter
              bookingTimes={bookingTimes}
              onCloseFilter={onCloseFilter}
              onSearch={onSearch}
              responsive={matches}
            />
          )}
        </MediaQuery>
      )}
      {message}
    </Container>
  )
}

export default Workspace

const StyledText = styled.div`
  font-size: 16px;
  font-weight: 600;
`
const StyledTitle = styled(StyledText)`
  text-align: center;
  margin-bottom: 6px;
`

const StyledButton = styled.button`
  font-size: 16px;
  font-weight: 600;
  box-sizing: border-box;
  background-color: #3395fe;
  border: 2px solid #3395fe;
  color: #ffffff;
  height: 46px;
  border-radius: 6px;
  outline: none;
  width: 90%;
`

const FilterContainer = styled.div`
  width: 90vw;

  @media (max-width: 420px) {
    height: 100%;
    width: 100%;
  }
`

const StyledSubTitle = styled(StyledText)`
  font-size: 14px;
  text-align: center;
  color: #a4a9b7;
  margin-bottom: 12px;
`
